import { useState, useEffect, useRef, useCallback } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { useMeta } from '@/contexts/MetaContext';
import { MetaMetrics } from '@/types/metaMetrics';
import { createInitialMetrics } from '@/utils/metricUtils';
import { fetchMetricsData } from '@/services/metaApi/metrics';
import { 
  isTooSoonForEndpoint, 
  getTimeUntilNextAllowedRequest, 
  formatTimeRemaining,
  trackSuccessfulRequest,
  handleRateLimitError,
  clearRateLimits,
  canForceRefresh,
  reduceRateLimitCooldown,
  shouldShowNotification
} from '@/utils/metaApiRateLimiting';

export type { PeriodData, CampaignDetailMetrics, MetaMetrics, PerformanceIndicator } from '@/types/metaMetrics';

export interface UseMetaMetricsOptions {
  refreshInterval?: number;
  dateRange?: {
    startDate: string;
    endDate: string;
  };
  skipFirstFetch?: boolean;
}

// Create a global metrics cache that persists between component mounts
const globalMetricsCache = new Map<string, {
  data: MetaMetrics;
  timestamp: number;
}>();

// Constants
const DEFAULT_FETCH_INTERVAL = 5 * 60 * 1000; // 5 minutes default
const CONNECTION_ERROR_THRESHOLD = 3; // Number of connection errors before auto-disconnect
const METRIC_CACHE_KEY = 'meta_metrics_cache';

// Try to load cached metrics from localStorage on module load
try {
  const savedCache = localStorage.getItem(METRIC_CACHE_KEY);
  if (savedCache) {
    const parsedCache = JSON.parse(savedCache);
    Object.keys(parsedCache).forEach(key => {
      globalMetricsCache.set(key, {
        data: parsedCache[key].data,
        timestamp: parsedCache[key].timestamp
      });
    });
    console.log('Loaded metrics cache from localStorage:', globalMetricsCache.size, 'items');
  }
} catch (e) {
  console.error('Error loading metrics cache from localStorage:', e);
}

// Helper function to save cache to localStorage
const saveMetricsCache = () => {
  try {
    const cacheObject: Record<string, any> = {};
    globalMetricsCache.forEach((value, key) => {
      cacheObject[key] = {
        data: value.data,
        timestamp: value.timestamp
      };
    });
    localStorage.setItem(METRIC_CACHE_KEY, JSON.stringify(cacheObject));
  } catch (e) {
    console.error('Error saving metrics cache to localStorage:', e);
  }
};

export const useMetaMetrics = (
  accessToken: string | null,
  options: UseMetaMetricsOptions = {}
) => {
  const [metrics, setMetrics] = useState<MetaMetrics>(createInitialMetrics());
  const { toast } = useToast();
  const { 
    disconnect, 
    selectedCampaignId, 
    incrementConnectionErrors, 
    resetConnectionErrors,
    connectionErrors,
    setConnected
  } = useMeta();
  
  const [lastFetchTime, setLastFetchTime] = useState<number>(0);
  const [isFetching, setIsFetching] = useState<boolean>(false);
  const [fetchAttempts, setFetchAttempts] = useState<number>(0);
  const [isRateLimited, setIsRateLimited] = useState<boolean>(false);
  const [rateLimitEndTime, setRateLimitEndTime] = useState<number>(0);
  const [nextRefreshTime, setNextRefreshTime] = useState<number>(0);
  const fetchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const fetchAbortController = useRef<AbortController | null>(null);
  
  // Use the refresh interval provided or default
  const FETCH_INTERVAL = options.refreshInterval || DEFAULT_FETCH_INTERVAL;
  // Maximum number of consecutive fetch attempts before showing error
  const MAX_FETCH_ATTEMPTS = 2;
  const fetchInProgress = useRef<boolean>(false);
  const prevCampaignId = useRef<string | null>(null);
  const mountedRef = useRef<boolean>(true);
  const forceRefreshRef = useRef<boolean>(false);
  const didInitialFetchRef = useRef<boolean>(false);
  
  // Reference to the component instance's metrics cache
  const metricsCache = useRef<Map<string, MetaMetrics>>(new Map());

  // Cleanup function to clear any pending timers and abort any in-flight requests
  const clearTimers = useCallback(() => {
    if (fetchTimeoutRef.current) {
      clearTimeout(fetchTimeoutRef.current);
      fetchTimeoutRef.current = null;
    }
    
    if (fetchAbortController.current) {
      fetchAbortController.current.abort();
      fetchAbortController.current = null;
    }
  }, []);
  
  // Function to determine if a cache entry is still valid
  const isCacheValid = useCallback((timestamp: number) => {
    return Date.now() - timestamp < FETCH_INTERVAL;
  }, [FETCH_INTERVAL]);

  // Function to get metrics from cache (global or component instance)
  const getFromCache = useCallback((campaignId: string | null) => {
    const cacheKey = `campaign_${campaignId || 'all'}`;
    
    // Try global cache first
    const globalCacheEntry = globalMetricsCache.get(cacheKey);
    if (globalCacheEntry && isCacheValid(globalCacheEntry.timestamp)) {
      console.log(`Using global cache for campaign ${campaignId || 'all'}`);
      return globalCacheEntry.data;
    }
    
    // Then try component instance cache
    if (metricsCache.current.has(cacheKey)) {
      console.log(`Using instance cache for campaign ${campaignId || 'all'}`);
      return metricsCache.current.get(cacheKey)!;
    }
    
    return null;
  }, [isCacheValid]);

  // Function to update cache
  const updateCache = useCallback((campaignId: string | null, data: MetaMetrics) => {
    const cacheKey = `campaign_${campaignId || 'all'}`;
    
    // Update both caches
    metricsCache.current.set(cacheKey, data);
    globalMetricsCache.set(cacheKey, {
      data,
      timestamp: Date.now()
    });
    
    // Save to localStorage
    saveMetricsCache();
  }, []);

  const fetchMetrics = useCallback(async (force = false) => {
    // Skip if skipFirstFetch is true and this is the first fetch
    if (options.skipFirstFetch && !didInitialFetchRef.current) {
      didInitialFetchRef.current = true;
      return;
    }

    // Check for rate limiting - with improved handling
    if (isRateLimited) {
      const now = Date.now();
      if (now < rateLimitEndTime) {
        console.log(`Meta API rate limited. Waiting before retrying.`);
        return;
      } else {
        // Rate limit period is over
        console.log('Rate limit period ended, resuming requests');
        setIsRateLimited(false);
        setRateLimitEndTime(0);
      }
    }
    
    // Check if it's too soon for a refresh (unless forced)
    if (!force) {
      const timeUntilRefresh = getTimeUntilNextAllowedRequest('meta_metrics');
      if (timeUntilRefresh > 0) {
        console.log(`Too soon for metrics refresh. Next refresh in ${formatTimeRemaining(timeUntilRefresh)}`);
        
        // Update next refresh time
        const nextRefresh = Date.now() + (timeUntilRefresh * 1000);
        setNextRefreshTime(nextRefresh);
        
        // Try to use cached data
        const cachedData = getFromCache(selectedCampaignId);
        if (cachedData) {
          setMetrics(cachedData);
        }
        return;
      }
    }
    
    // Prevent multiple simultaneous fetches
    if (fetchInProgress.current || isFetching) {
      console.log("Fetch already in progress, skipping");
      return;
    }
    
    if (!accessToken) {
      if (mountedRef.current) {
        setMetrics(prev => ({ ...prev, isLoading: false }));
        setFetchAttempts(0);
      }
      return;
    }
    
    // Check cache first if not forced refresh
    if (!force && selectedCampaignId !== null) {
      const cachedData = getFromCache(selectedCampaignId);
      if (cachedData) {
        setMetrics(cachedData);
        // Still update next refresh time
        setNextRefreshTime(Date.now() + FETCH_INTERVAL);
        return;
      }
    }
    
    // Set up a new AbortController for this fetch
    fetchAbortController.current = new AbortController();
    
    // Set a hard timeout to cancel the fetch operation after 8 seconds to prevent endless loading
    const fetchTimeoutId = setTimeout(() => {
      if (fetchAbortController.current) {
        console.log("Aborting fetch due to timeout (8s)");
        fetchAbortController.current.abort();
        
        if (mountedRef.current) {
          setIsFetching(false);
          fetchInProgress.current = false;
          
          // Try to use cached data if available
          const cachedData = getFromCache(selectedCampaignId);
          if (cachedData) {
            setMetrics(cachedData);
          } else {
            setMetrics(prev => ({ 
              ...prev, 
              isLoading: false,
              error: "Zeitüberschreitung bei der Datenabfrage. Verwende zwischengespeicherte Daten."
            }));
          }
        }
      }
    }, 8000); // 8 second hard timeout

    try {
      const currentTime = Date.now();
      const campaignChanged = prevCampaignId.current !== selectedCampaignId;
      
      // Skip if last fetch was recent AND campaign ID hasn't changed AND not forced
      if (!force && currentTime - lastFetchTime < FETCH_INTERVAL && lastFetchTime !== 0 && !campaignChanged) {
        console.log("Skipping metrics fetch, last fetch was recent and campaign hasn't changed");
        // Clear timeout if any
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
        }
        return;
      }
      
      if (campaignChanged) {
        console.log(`Campaign selection changed from ${prevCampaignId.current} to ${selectedCampaignId}`);
        
        // Try to use cached metrics for this campaign
        const cachedData = getFromCache(selectedCampaignId);
        if (cachedData) {
          setMetrics(cachedData);
          clearTimeout(fetchTimeoutId);
          prevCampaignId.current = selectedCampaignId;
          setFetchAttempts(0);
          return;
        }
        
        prevCampaignId.current = selectedCampaignId;
        setFetchAttempts(0); // Reset fetch attempts on campaign change
      }
      
      fetchInProgress.current = true;
      setIsFetching(true);
      
      if (mountedRef.current) {
        setMetrics(prev => ({ ...prev, isLoading: true, error: null }));
      }
      
      try {
        const metricsData = await fetchMetricsData({ 
          accessToken, 
          selectedCampaignId,
          dateRange: options.dateRange
        });
        
        // Clear timeout if any
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = null;
        }
        
        if (mountedRef.current) {
          setMetrics(metricsData);
          setLastFetchTime(currentTime);
          setFetchAttempts(0); // Reset fetch attempts on success
          resetConnectionErrors(); // Reset connection errors on successful fetch
          
          // Cache the metrics for this campaign
          updateCache(selectedCampaignId, metricsData);
          
          // Set next refresh time based on interval
          const nextRefresh = currentTime + FETCH_INTERVAL;
          setNextRefreshTime(nextRefresh);
          
          // Track successful request for rate limiting
          trackSuccessfulRequest('meta_metrics');
        }
      } catch (fetchError: any) {
        if (fetchTimeoutRef.current) {
          clearTimeout(fetchTimeoutRef.current);
          fetchTimeoutRef.current = null;
        }
        throw fetchError;
      }
    } catch (error: any) {
      // Handle timeout cleanup
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
        fetchTimeoutRef.current = null;
      }
      
      console.error('Error fetching Meta metrics:', error);
      
      // Check for rate limit errors
      if (error.message && (
          error.message.includes('limit reached') || 
          error.message.includes('Ratenbegrenzung'))) {
        console.log('Rate limit detected in metrics fetch');
        
        // Extract waiting time if available in error message
        let waitSeconds = 60; // Default to 1 minute (reduced from 5 minutes)
        const timeMatch = error.message.match(/(\d+) [Ss]ekunden/);
        if (timeMatch && timeMatch[1]) {
          waitSeconds = parseInt(timeMatch[1], 10);
        }
        
        if (waitSeconds > 0) {
          const waitTime = waitSeconds * 1000;
          setIsRateLimited(true);
          setRateLimitEndTime(Date.now() + waitTime);
          
          // Show toast about rate limiting - but only if we haven't shown too many
          if (shouldShowNotification('meta_metrics_rate_limit')) {
            toast({
              title: "Meta API Ratenbegrenzung",
              description: `Die Abfragerate wurde überschritten. Nächster Versuch in ${waitSeconds} Sekunden.`,
              variant: "destructive",
            });
          }
          
          // Use cached data if available
          const cachedData = getFromCache(selectedCampaignId);
          if (cachedData) {
            setMetrics(cachedData);
          }
        }
      } 
      else if (error.message && (
        error.message.toLowerCase().includes('token') ||
        error.message.toLowerCase().includes('unauthorized') ||
        error.message.toLowerCase().includes('unauthenticated') ||
        error.message.toLowerCase().includes('permission') ||
        error.status === 401 ||
        error.status === 403
      )) {
        // Handle authentication/authorization errors
        console.error('Meta authentication error:', error);
        
        // Increment connection errors counter
        incrementConnectionErrors();
        
        // If we've reached the threshold, disconnect automatically
        if (connectionErrors + 1 >= CONNECTION_ERROR_THRESHOLD) {
          console.error('Multiple authentication errors, disconnecting Meta account');
          toast({
            title: "Meta Verbindungsfehler",
            description: `Automatische Trennung nach mehrfachen Authentifizierungsfehlern.`,
            variant: "destructive",
          });
          
          // Force disconnect
          setTimeout(() => disconnect(), 100);
        } else {
          toast({
            title: "Meta Authentifizierungsfehler",
            description: `Bitte überprüfen Sie Ihre Meta-Verbindung.`,
            variant: "destructive",
          });
        }
      }
      else {
        // Only increment connection errors for network or API errors
        if (error instanceof Error && (
          error.message.includes('network') || 
          error.message.includes('API') ||
          error.message.includes('Netzwerk')
        )) {
          incrementConnectionErrors();
        }
      }
      
      // Don't increment if this was an abort error from our own timeout
      if (!(error instanceof DOMException && error.name === 'AbortError')) {
        const newAttemptCount = fetchAttempts + 1;
        setFetchAttempts(newAttemptCount);
      }
      
      if (mountedRef.current) {
        const errorMessage = error instanceof Error ? error.message : 'Unbekannter Fehler beim Abrufen der Metriken';
        
        // Try to use cached data first
        const cachedData = getFromCache(selectedCampaignId);
        if (cachedData) {
          setMetrics(cachedData);
        } else {
          // Show error in metrics only after multiple failures
          setMetrics(prev => ({ 
            ...prev, 
            isLoading: false, 
            error: fetchAttempts >= MAX_FETCH_ATTEMPTS ? errorMessage : null
          }));
        }
      }
    } finally {
      fetchInProgress.current = false;
      if (mountedRef.current) {
        setIsFetching(false);
      }
    }
  }, [accessToken, toast, disconnect, selectedCampaignId, options.dateRange, FETCH_INTERVAL, isFetching, fetchAttempts, MAX_FETCH_ATTEMPTS, lastFetchTime, clearTimers, isRateLimited, rateLimitEndTime, options.skipFirstFetch, connectionErrors, incrementConnectionErrors, resetConnectionErrors, getFromCache, updateCache]);

  // Set up initial fetch - with a small delay to prevent immediate API calls on first render
  useEffect(() => {
    mountedRef.current = true;
    
    const timer = setTimeout(() => {
      fetchMetrics();
    }, 800);  // Increased delay to allow other components to render first
    
    return () => {
      mountedRef.current = false;
      clearTimeout(timer);
      clearTimers();
    };
  }, [fetchMetrics, clearTimers]);

  // Effect to fetch when campaign changes, but with a delay and priority on using cached data
  useEffect(() => {
    if (selectedCampaignId !== undefined && prevCampaignId.current !== selectedCampaignId && didInitialFetchRef.current) {
      console.log("Campaign ID changed, checking cache first");
      
      // Check cache before triggering a refresh
      const cachedData = getFromCache(selectedCampaignId);
      if (cachedData) {
        setMetrics(cachedData);
        prevCampaignId.current = selectedCampaignId;
        return;
      }
      
      // If not in cache, refresh with delay
      forceRefreshRef.current = true;
      
      const timer = setTimeout(() => {
        fetchMetrics(false); // Use false to use cache first if it exists
        forceRefreshRef.current = false;
      }, 1000); // Larger delay for campaign change
      
      return () => clearTimeout(timer);
    }
  }, [selectedCampaignId, fetchMetrics, getFromCache]);

  // Method to manually refresh metrics with improved rate limit handling
  const refreshMetrics = useCallback(() => {
    if (fetchInProgress.current || !accessToken) return;
    
    // IMPROVED: Check if we can force refresh despite rate limiting
    if (isRateLimited) {
      // Check if we can override the rate limit (if it's been a while since last request)
      const canOverride = canForceRefresh('meta_metrics');
      
      if (canOverride) {
        // Allow override but only show warning toast if we're not showing too many
        if (shouldShowNotification('meta_refresh_force')) {
          toast({
            title: "Aktualisierung erzwingen",
            description: "Ratenbegrenzung wird umgangen.",
            variant: "default",
          });
        }
        
        // Reduce the cooldown time to make it more user-friendly - more aggressive reduction
        reduceRateLimitCooldown('meta_metrics', 0.3);
        setIsRateLimited(false);
        setRateLimitEndTime(0);
      } else {
        // Only show rate limit message if we haven't shown too many notifications
        if (shouldShowNotification('meta_rate_limit_error')) {
          const remainingTime = Math.ceil((rateLimitEndTime - Date.now()) / 1000);
          toast({
            title: "Aktualisierung nicht möglich",
            description: `Bitte warten Sie ${formatTimeRemaining(remainingTime)}.`,
            variant: "default",
          });
        }
        return;
      }
    }
    
    // Check if it's too soon for a refresh, with ability to override
    const timeUntilRefresh = getTimeUntilNextAllowedRequest('meta_metrics');
    if (timeUntilRefresh > 0) {
      // If it's been more than 30 seconds since our last request, allow forcing anyway
      const canOverride = canForceRefresh('meta_metrics');
      
      if (canOverride) {
        // Allow the refresh with reduced toast notifications
        if (shouldShowNotification('meta_refresh_override')) {
          toast({
            title: "Aktualisierung",
            description: "Daten werden aktualisiert...",
            variant: "default",
          });
        }
      } else {
        // Only show this message if we haven't shown too many notifications
        if (shouldShowNotification('meta_refresh_wait')) {
          toast({
            title: "Bitte warten",
            description: `Nächste Aktualisierung in ${formatTimeRemaining(timeUntilRefresh)}.`,
            variant: "default",
          });
        }
        return;
      }
    }
    
    console.log("Manual refresh triggered");
    setFetchAttempts(0); // Reset fetch attempts on manual refresh
    forceRefreshRef.current = true;

    // Reduce rate limiting for this endpoint to allow more immediate refresh
    reduceRateLimitCooldown('meta_metrics', 0.3);
    
    setTimeout(() => {
      fetchMetrics(true);
      forceRefreshRef.current = false;
    }, 100);
    
    toast({
      title: "Aktualisierung",
      description: "Metriken werden aktualisiert...",
      variant: "default",
    });
  }, [fetchMetrics, accessToken, isRateLimited, rateLimitEndTime, toast]);

  return {
    ...metrics,
    refreshMetrics,
    isFetching,
    lastFetchTime,
    fetchAttempts,
    forceRefreshing: forceRefreshRef.current,
    isRateLimited,
    rateLimitEndTime,
    nextRefreshTime
  };
};
